import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import { DarkTiles } from "@/components/map/DarkTiles";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Search, ArrowLeft, Navigation2, X, Check } from "lucide-react";

// Icône de pin ARTEKNOS (bleu primaire)
const pinIcon = new L.DivIcon({
  className: "",
  html: `<div style="width:40px;height:52px;position:relative;">
    <div style="position:absolute;left:50%;top:0;transform:translateX(-50%);width:32px;height:32px;background:#003594;border-radius:50% 50% 50% 0;transform:translateX(-50%) rotate(-45deg);box-shadow:0 6px 16px rgba(0,71,171,0.5);display:flex;align-items:center;justify-content:center;">
      <div style="width:10px;height:10px;background:white;border-radius:50%;transform:rotate(45deg);"></div>
    </div>
  </div>`,
  iconSize: [40, 52],
  iconAnchor: [20, 32],
});

// eslint-disable-next-line no-unused-vars
function ClickHandler({ onSelect }) {
  useMapEvents({
    click(e) {
      onSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function RecenterButton({ position }) {
  const map = useMap();
  return (
    <button
      onClick={() => {
        if (position) map.setView(position, 15, { animate: true });
      }}
      className="absolute bottom-4 right-4 z-[400] w-12 h-12 rounded-full bg-[#003594] hover:bg-[#003380] shadow-xl flex items-center justify-center text-white transition-all active:scale-95"
      data-testid="recenter-btn"
      aria-label="Centrer sur ma position"
    >
      <Navigation2 className="w-5 h-5" />
    </button>
  );
}

export default function DestinationPicker({ onClose, onConfirm, initialCenter }) {
  const [position, setPosition] = useState(initialCenter || [14.6928, -17.4467]);
  const [address, setAddress] = useState("");
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [myPos, setMyPos] = useState(null);
  const searchDebounce = useRef(null);

  useEffect(() => {
    // Géoloc utilisateur au montage
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const p = [pos.coords.latitude, pos.coords.longitude];
          setMyPos(p);
          if (!initialCenter) {
            setPosition(p);
            reverseGeocode(p[0], p[1]);
          }
        },
        () => {},
        { timeout: 5000 }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    reverseGeocode(position[0], position[1]);
  }, [position]);

  const reverseGeocode = async (lat, lng) => {
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const d = await r.json();
      if (d.display_name) setAddress(d.display_name);
    } catch {
      /* ignore */
    }
  };

  const handleSearchChange = (v) => {
    setQuery(v);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (!v || v.length < 3) {
      setSuggestions([]);
      return;
    }
    searchDebounce.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            v
          )}&countrycodes=sn&limit=6`
        );
        const d = await r.json();
        setSuggestions(d || []);
      } catch {
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 350);
  };

  const selectSuggestion = (s) => {
    const lat = parseFloat(s.lat);
    const lng = parseFloat(s.lon);
    setPosition([lat, lng]);
    setAddress(s.display_name);
    setQuery("");
    setSuggestions([]);
  };

  const handleConfirm = () => {
    if (onConfirm) onConfirm(position[0], position[1], address);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#101010] flex flex-col"
      data-testid="destination-picker"
    >
      {/* HEADER */}
      <header className="relative px-4 pt-4 pb-3 border-b border-[#2D2D2D]">
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#1A1A1A] text-white transition-colors"
            data-testid="picker-close-btn"
            aria-label="Retour"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2
            className="text-white font-bold text-lg flex-1"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Où allons-nous ?
          </h2>
        </div>

        {/* SEARCH */}
        <div className="relative">
          <div className="flex items-center bg-[#1A1A1A] rounded-2xl px-4 py-3">
            <Search className="w-5 h-5 text-[#A0A0A0] flex-shrink-0" />
            <input
              type="text"
              value={query}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Rechercher une adresse, un quartier..."
              className="flex-1 ml-3 bg-transparent text-white placeholder-[#A0A0A0] outline-none text-base"
              data-testid="picker-search-input"
            />
            {query && (
              <button
                onClick={() => {
                  setQuery("");
                  setSuggestions([]);
                }}
                className="w-6 h-6 rounded-full bg-[#2D2D2D] flex items-center justify-center"
                aria-label="Effacer"
              >
                <X className="w-3.5 h-3.5 text-white" />
              </button>
            )}
          </div>

          {/* SUGGESTIONS dropdown */}
          {(isLoading || suggestions.length > 0) && (
            <div
              className="absolute left-0 right-0 top-full mt-2 bg-[#1A1A1A] rounded-2xl border border-[#2D2D2D] shadow-2xl max-h-80 overflow-y-auto z-10"
              data-testid="picker-suggestions"
            >
              {isLoading && (
                <div className="px-4 py-3 text-[#A0A0A0] text-sm">
                  Recherche...
                </div>
              )}
              {!isLoading &&
                suggestions.map((s) => (
                  <button
                    key={s.place_id}
                    onClick={() => selectSuggestion(s)}
                    className="flex items-start gap-3 px-4 py-3 hover:bg-[#2D2D2D] w-full text-left border-b border-[#2D2D2D] last:border-b-0 transition-colors"
                    data-testid={`suggestion-${s.place_id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-[#003594]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MapPin className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">
                        {s.display_name.split(",")[0]}
                      </p>
                      <p className="text-[#A0A0A0] text-xs truncate">
                        {s.display_name.split(",").slice(1, 3).join(",")}
                      </p>
                    </div>
                  </button>
                ))}
            </div>
          )}
        </div>
      </header>

      {/* MAP */}
      <div className="flex-1 relative">
        <div className="absolute inset-0 z-0 isolate">
          <MapContainer
            center={position}
            zoom={14}
            style={{ height: "100%", width: "100%", background: "#101010" }}
            zoomControl={false}
            attributionControl={false}
          >
            <DarkTiles />
            <ClickHandler onSelect={(lat, lng) => setPosition([lat, lng])} />
            <Marker position={position} icon={pinIcon} />
            <RecenterButton position={myPos} />
          </MapContainer>
        </div>

        {/* Indication centre (non cliquable, fixe au centre du viewport map) */}
        <div className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur px-4 py-2 rounded-full z-[400]">
          <p className="text-white text-xs font-medium">
            Touchez la carte pour poser votre pin
          </p>
        </div>
      </div>

      {/* BOTTOM — adresse + bouton confirmer */}
      <div className="bg-[#1A1A1A] border-t border-[#2D2D2D] p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#003594]/20 flex items-center justify-center flex-shrink-0">
            <MapPin className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[#A0A0A0] text-xs mb-0.5">Destination</p>
            <p
              className="text-white text-sm font-medium line-clamp-2"
              data-testid="picker-address-display"
            >
              {address || "Sélectionnez un point sur la carte"}
            </p>
          </div>
        </div>
        <button
          onClick={handleConfirm}
          disabled={!address}
          className="w-full bg-gradient-to-r from-[#003594] to-[#D4AF37] hover:from-[#003380] hover:to-[#0099BF] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-2xl py-4 flex items-center justify-center gap-2 shadow-lg shadow-[#003594]/30 transition-all active:scale-95"
          data-testid="picker-confirm-btn"
        >
          <Check className="w-5 h-5" />
          Confirmer cette destination
        </button>
      </div>
    </div>
  );
}
